import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Sparkles, ArrowRight, Mail, Clock, CheckCircle2, Eye, AlertCircle, Send } from "lucide-react";
import { analytics, getVariant } from "@/lib/analytics";
import { MultiLayerValue } from "@/components/welcome/MultiLayerValue";
import { InvestorPitchDeck } from "@/components/welcome/InvestorPitchDeck";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function WelcomeInvestor() {
  const [params] = useSearchParams();
  const rawName = params.get("name");
  const name = rawName || "there";
  const hasName = Boolean(rawName);
  const appId = params.get("app_id") || "";
  const variant = getVariant("welcome_investor");
  const [statusInfo, setStatusInfo] = useState(null);
  const [polling, setPolling] = useState(false);
  // Demo-interest signal state
  const [interestRequested, setInterestRequested] = useState(false);
  const [interestSubmitting, setInterestSubmitting] = useState(false);
  const [interestMsg, setInterestMsg] = useState("");

  useEffect(() => {
    analytics.rolePreviewViewed("investor", variant);
    analytics.investorPitchViewed("pitch-deck-loaded");
  }, [variant]);

  const fetchStatus = useCallback(async () => {
    if (!appId) return;
    try {
      const { data } = await axios.get(`${API}/public/application-status/${appId}`);
      setStatusInfo(data);
    } catch (err) {
      // Status poll is best-effort — surface in dev console only so a
      // transient 502 doesn't spam Sentry/production logs.
      if (process.env.NODE_ENV === "development") console.warn("status poll failed", err);
    }
  }, [appId]);
  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Hydrate demo_interest state on mount + after refresh
  useEffect(() => {
    if (!appId) return;
    let alive = true;
    axios.get(`${API}/public/application-interest/${appId}`)
      .then((r) => { if (alive && r.data?.demo_interest) setInterestRequested(true); })
      .catch((err) => {
        if (process.env.NODE_ENV === "development") console.warn("interest hydrate failed", err);
      });
    return () => { alive = false; };
  }, [appId]);

  // Auto-poll every 30s while pending so the UI flips to approved without a manual click
  useEffect(() => {
    if (!appId || statusInfo?.status === "approved") return;
    const t = setInterval(fetchStatus, 30000);
    return () => clearInterval(t);
  }, [appId, statusInfo?.status, fetchStatus]);

  const checkAgain = async () => {
    setPolling(true);
    await fetchStatus();
    setTimeout(() => setPolling(false), 600);
  };

  const requestDemoAccess = async () => {
    if (!appId) {
      setInterestMsg("We need your application reference to record interest. Please apply first from the homepage.");
      return;
    }
    if (interestRequested || interestSubmitting) return;
    setInterestSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/applications/express-interest`, { application_id: appId });
      setInterestRequested(true);
      setInterestMsg(data?.message || "Thanks — your interest is noted. Our team will be in touch within 24 hours.");
      analytics.demoRequested("investor", appId);
      analytics.ctaClicked("Request Demo Access", "welcome-investor");
    } catch (e) {
      setInterestMsg("Could not record interest right now. Please try again in a moment.");
    } finally {
      setInterestSubmitting(false);
    }
  };

  const isApproved = statusInfo?.status === "approved";
  const demoUrl = statusInfo?.demo_url || statusInfo?.investor_demo_url;
  const fundName = statusInfo?.company || "";

  return (
    <div className="min-h-screen bg-[#050A07] text-white overflow-hidden" data-testid="welcome-investor">
      {/* Layered backdrop */}
      <div className="absolute inset-0 [background-image:radial-gradient(circle_at_50%_-10%,rgba(34,197,94,0.18),transparent_60%)]" />
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(34,197,94,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(34,197,94,0.4)_1px,transparent_1px)] [background-size:80px_80px]" />

      {/* Header */}
      <header className="relative border-b border-[#1A2E22]">
        <div className="max-w-[1100px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" aria-label="Delta GreenX home">
            <BrandLogo as="div" size="md" />
          </Link>
          <Link to="/" className="text-[#A1B2A8] hover:text-white text-xs flex items-center gap-1">← Back to landing</Link>
        </div>
      </header>

      <section className="relative max-w-[1100px] mx-auto px-6 pt-16 pb-24">
        <motion.div {...fade(0)} className="flex items-center gap-2 mb-5">
          {isApproved ? (
            <><CheckCircle2 className="w-4 h-4 text-[#22C55E]" /><span className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-semibold">Approved · Confidential Access Granted</span></>
          ) : (
            <><Clock className="w-4 h-4 text-yellow-400" /><span className="text-yellow-400 text-[10px] uppercase tracking-[0.3em] font-semibold">Application Received · Under Review</span></>
          )}
        </motion.div>

        <motion.h1 {...fade(0.05)} className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
          {isApproved
            ? <>Welcome aboard, <span className="text-[#22C55E]">{name}</span>{fundName ? <> from <span className="text-white">{fundName}</span></> : null}.</>
            : hasName
              ? <>Thank you, <span className="text-[#22C55E]">{name}</span>. <br className="hidden sm:block" />Your access is being prepared.</>
              : <>Thank you. <br className="hidden sm:block" />Your access is being prepared.</>}
        </motion.h1>

        <motion.p {...fade(0.12)} className="text-[#C5D5CC] text-base sm:text-lg mt-6 max-w-2xl leading-relaxed">
          {isApproved
            ? "Your personalized, read-only demo link is ready below. Inside, you'll see the full operating infrastructure: capital lifecycle, AI confidence scoring, supply-chain mapping, pilot economics, 5-year scale model, and the term-sheet-ready ask."
            : "Investor access on Delta GreenX is by application only. Our team typically reviews within 24 hours. You'll receive an email with a personalized live-demo link the moment your access is approved."}
        </motion.p>

        {/* Approved State — Personalized Demo CTA */}
        {isApproved && demoUrl && (
          <motion.div {...fade(0.2)} className="mt-10 border border-[#22C55E]/40 bg-[#22C55E]/5 p-8" data-testid="welcome-investor-approved">
            <div className="flex items-start gap-4 mb-5">
              <Sparkles className="w-6 h-6 text-[#22C55E] mt-1 flex-shrink-0" />
              <div>
                <p className="font-['Outfit'] text-2xl font-bold text-white">Your personalized live demo is ready.</p>
                <p className="text-[#A1B2A8] text-sm mt-1">Read-only · 30-min session · pre-loaded with $825K of live capital flows, AI scoring, and the full lifecycle returns dashboard.</p>
              </div>
            </div>
            <a
              href={demoUrl}
              onClick={() => analytics.ctaClicked("Open Personalized Demo", "welcome-investor")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#22C55E] text-[#050A07] font-['Outfit'] font-semibold text-sm hover:bg-[#1CAB4D] transition-colors"
              data-testid="welcome-investor-open-demo"
            >
              <Eye className="w-4 h-4" /> Open Live Demo <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-[#A1B2A8]/40 text-[10px] mt-3 break-all font-mono">{demoUrl}</p>
          </motion.div>
        )}

        {/* Slide-9 multi-layer reveal — INVESTOR (only when approved) */}
        {isApproved && <MultiLayerValue persona="investor" />}

        {/* Pending State — Locked Tease + Status */}
        {!isApproved && (
          <>
            {/* What you'll get — locked teaser tiles */}
            <motion.div {...fade(0.18)} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="welcome-investor-pending-tiles">
              {[
                { label: "Pilot economics", value: "•••" },
                { label: "5-year scale model", value: "•••" },
                { label: "Live operating demo", value: "•••" },
              ].map((t, i) => (
                <motion.div key={i} {...fade(0.22 + i * 0.05)}
                  className="relative border border-[#1A2E22] bg-[#09120D] p-6 overflow-hidden">
                  <div className="absolute inset-0 backdrop-blur-[2px] bg-[#050A07]/60" />
                  <div className="relative text-center">
                    <Lock className="w-4 h-4 text-[#22C55E]/50 mb-3 mx-auto" />
                    <p className="font-['Outfit'] text-3xl font-bold text-[#22C55E]/40 tracking-[0.2em]">{t.value}</p>
                    <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider mt-2">{t.label}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Status panel */}
            <motion.div {...fade(0.35)} className="mt-10 border border-[#1A2E22] bg-[#09120D] p-6 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3">What happens next</p>
                <ol className="space-y-3">
                  {[
                    { n: "01", title: "Application reviewed", desc: "Our team verifies fund fit and accreditation. Typically <24h.", done: !!statusInfo },
                    { n: "02", title: "Personalized demo link emailed", desc: "Read-only live walkthrough — your name, your fund, your access only.", done: false },
                    { n: "03", title: "1:1 walkthrough call (optional)", desc: "Founding team walks you through pilot economics, scale model, and term-sheet conversation.", done: false },
                  ].map((s, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className={`font-['Outfit'] text-lg font-bold w-8 flex-shrink-0 ${s.done ? "text-[#22C55E]" : "text-[#A1B2A8]/40"}`}>{s.n}</span>
                      <div>
                        <p className={`text-sm font-semibold ${s.done ? "text-white" : "text-[#A1B2A8]"}`}>
                          {s.title} {s.done && <CheckCircle2 className="inline w-3.5 h-3.5 ml-1 text-[#22C55E]" />}
                        </p>
                        <p className="text-[#A1B2A8]/60 text-xs mt-0.5">{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="border-l border-[#1A2E22] pl-6">
                <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3">Status</p>
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                  <span className="text-yellow-400 font-['Outfit'] font-semibold text-sm">Under review</span>
                </div>
                {appId && (
                  <p className="text-[#A1B2A8]/40 text-[10px] mb-4 font-mono break-all">App ref: {appId.slice(0, 8)}…</p>
                )}
                <button
                  onClick={checkAgain}
                  disabled={polling}
                  className="text-xs text-[#22C55E] border border-[#22C55E]/30 px-3 py-1.5 hover:bg-[#22C55E]/10 disabled:opacity-50 flex items-center gap-1"
                  data-testid="welcome-investor-recheck"
                >
                  {polling ? "Checking…" : "Check again"}
                </button>
                <p className="text-[#A1B2A8]/50 text-[10px] mt-4 leading-relaxed flex items-start gap-1.5">
                  <Mail className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  We'll email <span className="text-white">{statusInfo?.full_name || "you"}</span> the moment your access is approved.
                </p>
              </div>
            </motion.div>

            <motion.div {...fade(0.45)} className="mt-8 text-center">
              <p className="text-[#A1B2A8]/40 text-[10px] uppercase tracking-[0.25em]">
                Confidential by design · Released only after admin approval
              </p>
            </motion.div>

            {/* ============== INVESTOR PITCH DECK (slides 13 / 10 / 11 / 12) ============== */}
            <motion.div {...fade(0.55)} className="mt-12 mb-2 text-center">
              <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-bold mb-2">
                While you wait — explore the thesis
              </p>
              <p className="text-[#A1B2A8] text-sm">
                The big picture · pilot economics · market size · 5-year scale model
              </p>
            </motion.div>
          </>
        )}
      </section>

      {/* Pending pitch deck (rendered outside the narrow container) */}
      {!isApproved && <InvestorPitchDeck />}

      {/* Demo-Access Request — pending only */}
      {!isApproved && (
        <section className="relative max-w-[1100px] mx-auto px-6 pb-24 pt-12">
          <motion.div
            {...fade(0)}
            className={`border ${interestRequested ? "border-[#22C55E]/40 bg-[#22C55E]/5" : "border-[#22C55E]/30 bg-gradient-to-br from-[#0d1f14] to-[#070F0A]"} p-6 sm:p-10`}
            data-testid="welcome-investor-demo-cta"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1 max-w-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-[#22C55E]" />
                  <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-bold">
                    {interestRequested ? "Demo access requested" : "Still interested? Ask for live demo access"}
                  </p>
                </div>
                <h3 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white leading-tight">
                  {interestRequested
                    ? "Thanks — your interest is noted."
                    : <>See how we operate. <br className="hidden sm:block" /><span className="text-[#22C55E]">Live, not slides.</span></>}
                </h3>
                <p className="text-[#A1B2A8] text-sm mt-3 leading-relaxed">
                  {interestRequested
                    ? (interestMsg || "Our team will reach out shortly. Once approved, you'll receive a personalized read-only walkthrough of our operating infrastructure — capital lifecycle, AI confidence scoring, supply-chain mapping, and live pilot economics.")
                    : "Approved investors receive a personalized, read-only 30-minute walkthrough of our operating infrastructure — capital deployed, AI confidence scoring, supply-chain mapping, and live pilot economics. Demo access is granted only after our team verifies fund fit."}
                </p>
              </div>

              <div className="flex flex-col items-stretch lg:items-end gap-2 flex-shrink-0">
                {!interestRequested ? (
                  <button
                    onClick={requestDemoAccess}
                    disabled={interestSubmitting}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#22C55E] text-[#050A07] font-['Outfit'] font-semibold text-sm hover:bg-[#1CAB4D] disabled:opacity-50 transition-colors"
                    data-testid="welcome-investor-request-demo-btn"
                  >
                    {interestSubmitting ? "Recording…" : <><Send className="w-4 h-4" /> Request Demo Access <ArrowRight className="w-4 h-4" /></>}
                  </button>
                ) : (
                  <span className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E] font-['Outfit'] font-semibold text-sm" data-testid="welcome-investor-request-confirmed">
                    <CheckCircle2 className="w-4 h-4" /> Interest received
                  </span>
                )}
                <p className="text-[#A1B2A8]/40 text-[10px] mt-1 lg:text-right">
                  Reach us directly: <a href="mailto:hello@deltagreenx.com" className="text-[#22C55E] hover:underline">hello@deltagreenx.com</a>
                </p>
              </div>
            </div>
            {interestMsg && !interestRequested && (
              <p className="text-yellow-400/80 text-xs mt-4" data-testid="welcome-investor-request-error">{interestMsg}</p>
            )}
          </motion.div>
        </section>
      )}

      <section className="relative max-w-[1100px] mx-auto px-6 pb-12">
        {/* Status not found */}
        {statusInfo?.ok === false && (
          <motion.div {...fade(0.2)} className="border border-yellow-400/30 bg-yellow-400/5 p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-400 font-semibold text-sm">Application reference not found</p>
              <p className="text-[#A1B2A8] text-xs mt-1">If you&apos;ve already applied, please use the link from your confirmation email.</p>
            </div>
          </motion.div>
        )}
      </section>
    </div>
  );
}
